import React, { useState } from 'react';
import './EbayScraper.css';
import API_BASE_URL from '../config/apiBaseUrl';

const getAuthHeaders = (headers = {}) => {
  const token = localStorage.getItem('authToken');
  return token ? { ...headers, Authorization: `Bearer ${token}` } : headers;
};

const formatCurrency = (value) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return 'N/A';
  }

  return `${Number(value).toFixed(2)} EUR`;
};

const getToneClass = (sellerMedian, globalMedian) => {
  if (!sellerMedian || !globalMedian) {
    return '';
  }

  const diffPercent = ((sellerMedian - globalMedian) / globalMedian) * 100;
  if (diffPercent <= -10) {
    return 'is-low';
  }
  if (diffPercent >= 10) {
    return 'is-high';
  }

  return 'is-neutral';
};

const EbayScraper = () => {
  const [reference, setReference] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const handleSearch = async (event) => {
    event.preventDefault();

    const normalizedReference = reference.trim();
    if (!normalizedReference) {
      setError('Veuillez renseigner une reference OEM.');
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const response = await fetch(
        `${API_BASE_URL}/comparison/live/${encodeURIComponent(normalizedReference)}?maxResults=200`,
        {
          headers: getAuthHeaders(),
        }
      );

      const data = await response.json();

      if (!response.ok || !data.success) {
        setError(data.error || data.message || 'Erreur lors de la recherche eBay.');
        return;
      }

      setResult(data.data);
    } catch (err) {
      setError(`Erreur de connexion: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const competitors = result?.competitors || [];
  const vendorsWithResults = competitors.filter((competitor) => competitor.itemCount > 0).length;

  return (
    <div className="ebay-scraper">
      <div className="scraper-header">
        <h1>Analyse Ref OEM</h1>
        <p>
          Recherche ponctuelle pour une seule reference OEM, compare à tous les vendeurs enregistrés, sans sauvegarde en base.
        </p>
      </div>

      <div className="scraper-container">
        <div className="scraper-form-section">
          <h2>Recherche live</h2>
          <form onSubmit={handleSearch} className="scraper-form">
            <div className="form-group">
              <label htmlFor="reference">Reference OEM</label>
              <input
                id="reference"
                type="text"
                placeholder="Ex: 9829526780"
                value={reference}
                onChange={(event) => setReference(event.target.value.toUpperCase())}
                disabled={loading}
                className="input-field"
              />
              <small>
                La recherche est lancee sur l ensemble des vendeurs actifs deja configures, avec une limite de 200
                resultats sur l appel API.
              </small>
            </div>

            <div className="search-hints">
              <span className="hint-chip">Source vendeurs: Veille Marche</span>
              <span className="hint-chip">Persistance: desactivee</span>
              <span className="hint-chip">Statistiques: medianes par vendeur + globale</span>
            </div>

            <button
              type="submit"
              disabled={loading || !reference.trim()}
              className="scrape-btn"
            >
              {loading ? 'Analyse en cours...' : 'Comparer cette reference'}
            </button>
          </form>
        </div>

        {error && (
          <div className="error-message">
            <strong>Erreur:</strong> {error}
          </div>
        )}

        {result && (
          <>
            <div className="results-summary live-summary">
              <h3>Resume de la comparaison</h3>
              <div className="summary-stats">
                <div className="stat">
                  <span className="stat-label">Ref OEM</span>
                  <span className="stat-value">{result.oemReference}</span>
                </div>
                <div className="stat">
                  <span className="stat-label">Vendeurs interroges</span>
                  <span className="stat-value">{result.sellerCount}</span>
                </div>
                <div className="stat">
                  <span className="stat-label">Vendeurs avec resultats</span>
                  <span className="stat-value">{vendorsWithResults}</span>
                </div>
                <div className="stat">
                  <span className="stat-label">Annonces recues</span>
                  <span className="stat-value">{result.totalItems}</span>
                </div>
                <div className="stat">
                  <span className="stat-label">Mediane globale eBay</span>
                  <span className="stat-value">{formatCurrency(result.ebayMedianGlobal)}</span>
                </div>
                <div className="stat">
                  <span className="stat-label">Notre total</span>
                  <span className="stat-value">
                    {result.hasInternalStock ? formatCurrency(result.ourTotalPrice) : 'Absent du stock'}
                  </span>
                </div>
              </div>
            </div>

            <div className="results-section">
              <h2>Comparaison par vendeur</h2>

              <div className="vendor-grid">
                {competitors.map((competitor) => (
                  <div
                    key={competitor.name}
                    className={`vendor-card ${getToneClass(competitor.ebayMedianTotalPrice, result.ebayMedianGlobal)}`}
                  >
                    <div className="vendor-card-header">
                      <div>
                        <h3>{competitor.name}</h3>
                        <p>{competitor.itemCount} annonce(s) retenue(s)</p>
                      </div>
                      <span className="vendor-badge">
                        {competitor.itemCount > 0 ? 'Actif' : 'Aucun resultat'}
                      </span>
                    </div>

                    <div className="vendor-metrics">
                      <div className="metric-line">
                        <span>Mediane vendeur</span>
                        <strong>{formatCurrency(competitor.ebayMedianTotalPrice)}</strong>
                      </div>
                      <div className="metric-line">
                        <span>Mini</span>
                        <strong>{formatCurrency(competitor.minPrice)}</strong>
                      </div>
                      <div className="metric-line">
                        <span>Maxi</span>
                        <strong>{formatCurrency(competitor.maxPrice)}</strong>
                      </div>
                    </div>

                    {competitor.itemCount > 0 ? (
                      <div className="vendor-items">
                        {competitor.results.slice(0, 5).map((item) => (
                          <div key={item.itemId} className="vendor-item-row">
                            <div className="vendor-item-main">
                              <strong>{item.totalPrice ? formatCurrency(item.totalPrice) : formatCurrency(item.price)}</strong>
                              <span>{item.title || 'Annonce sans titre'}</span>
                            </div>
                            {item.link && (
                              <a href={item.link} target="_blank" rel="noopener noreferrer" className="item-link">
                                Voir
                              </a>
                            )}
                          </div>
                        ))}
                        {competitor.itemCount > 5 && (
                          <p className="vendor-more">+ {competitor.itemCount - 5} annonce(s) supplementaire(s)</p>
                        )}
                      </div>
                    ) : (
                      <p className="no-results">Aucune annonce retournee pour ce vendeur sur cette reference.</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default EbayScraper;
